import { esc } from "./format.js";
import { sfx } from "../app/audio.js";

/**
 * Cinematics (Phase 8): short, skippable full-screen moments drawn in
 * SVG/CSS, so they're identical in Lite and 3D.
 *
 *   cinemaMomentIntro(moment, current)   "Tonight: Keynote Day"           ~1.4 s
 *   cinemaMomentReveal(result, moment)   the number rolls up, then verdict ~2.6 s
 *   cinemaBoardVote(desk)                each director votes, then the tally ~2.8 s
 *   cinemaIpoBell(info)                  the opening bell rings            ~2.4 s
 *
 * Tap, click or Esc skips to the end; reduced motion shows the end state.
 * Each returns a Promise that resolves when it's closed.
 */
const CN_REDUCED = () => !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);

function cnStage(cls, html, { hold = 2600, closeLabel = "Continue" } = {}) {
  return new Promise((resolve) => {
    const el = document.createElement("div");
    el.className = `cn-stage ${cls}${CN_REDUCED() ? " cn-final" : ""}`;
    el.setAttribute("role", "dialog");
    el.innerHTML = `<div class="cn-inner">${html}</div><button type="button" class="cn-skip">${CN_REDUCED() ? closeLabel : "Skip ▸"}</button>`;
    document.body.appendChild(el);
    let done = false, finalShown = CN_REDUCED();
    const toFinal = () => { if (finalShown) return; finalShown = true; el.classList.add("cn-final"); el.dispatchEvent(new CustomEvent("cn:final")); el.querySelector(".cn-skip").textContent = closeLabel; };
    const close = () => { if (done) return; done = true; el.classList.add("cn-out"); setTimeout(() => { el.remove(); resolve(); }, CN_REDUCED() ? 0 : 220); document.removeEventListener("keydown", onKey); };
    const onKey = (e) => { if (e.key === "Escape" || e.key === "Enter" || e.key === " ") { e.preventDefault(); finalShown ? close() : toFinal(); } };
    el.addEventListener("click", () => (finalShown ? close() : toFinal()));
    document.addEventListener("keydown", onKey);
    if (!CN_REDUCED()) setTimeout(toFinal, hold);
    el._toFinal = toFinal;
  });
}

/** Animate a number in `node` from a to b over ms; jumps when the stage goes final. */
function cnRoll(stage, node, a, b, decimals, unit, ms = 1600) {
  const fmt = (v) => `${(+v).toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}${unit}`;
  if (CN_REDUCED()) { node.textContent = fmt(b); return; }
  const t0 = performance.now();
  const step = (now) => {
    if (!node.isConnected) return;
    if (stage.classList.contains("cn-final")) { node.textContent = fmt(b); return; }
    const t = Math.min(1, (now - t0) / ms), e = 1 - Math.pow(1 - t, 3);
    node.textContent = fmt(a + (b - a) * e);
    if (t < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

function cinemaMomentIntro(moment, current) {
  const ctx = (current && current.context) || {};
  sfx("moment");
  return cnStage("cn-intro", `
    <div class="cn-icon">${esc(moment.icon)}</div>
    <div class="cn-kicker">${ctx.reason === "launch" ? `Your launch of ${esc(ctx.product || "a new product")} flows into` : "Signature Moment"}</div>
    <h2 class="cn-title">${esc(moment.title)}</h2>
    <p class="cn-tag">${esc(moment.tagline)}</p>`, { hold: 1400, closeLabel: "Make your call" });
}

function cinemaMomentReveal(result, moment) {
  const rv = result.reveal || {};
  setTimeout(() => sfx(rv.value == null ? "soft" : result.success ? "win" : "lose"), CN_REDUCED() ? 0 : 1500);
  const lo = Math.min(...(rv.lose || [0]), ...(rv.win || [0]));
  const p = cnStage(`cn-reveal ${result.success ? "cn-win" : "cn-lose"}`, `
    <div class="cn-icon">${esc((moment && moment.icon) || "⭐")}</div>
    <div class="cn-kicker">${esc(result.title)} · ${esc(result.choice)}</div>
    ${rv.value == null ? "" : `<div class="cn-meter"><span>${esc(rv.label || "")}</span><b class="cn-num">0</b></div>`}
    <div class="cn-verdict"><em>${rv.value == null ? "PLAYED SAFE" : result.success ? "SUCCESS" : "IT WENT WRONG"}</em><p>${esc(result.text)}</p><small>${esc(result.effects || "")}</small></div>`,
  { hold: 2600 });
  const stages = document.querySelectorAll(".cn-stage"), el = stages[stages.length - 1]; // cnStage builds it synchronously
  if (rv.value != null) cnRoll(el, el.querySelector(".cn-num"), lo, rv.value, rv.decimals || 0, rv.unit || "");
  return p;
}

function cinemaBoardVote(desk) {
  const dirs = (desk.directors || []).slice(0, 8);
  const backs = dirs.filter((d) => d.mood >= 50).length;
  sfx("board");
  const p = cnStage("cn-board", `
    <div class="cn-kicker">Quarterly Board review · month ${esc(desk.month)}</div>
    <h2 class="cn-title">The Board votes</h2>
    <div class="cn-votes">${dirs.map((d, i) => `
      <div class="cn-vote ${d.mood >= 50 ? "yes" : "no"}" style="--i:${i}"><div class="cn-card"><div class="cn-front">${esc(String(d.name).split(" ").map((x) => x[0]).join("").slice(0, 2))}</div>
        <div class="cn-back">${d.mood >= 50 ? "✔" : "✖"}</div></div><span>${esc(String(d.name).split(" ").slice(-1)[0])}</span><em>${esc(d.mood_label)}</em></div>`).join("")}</div>
    <div class="cn-tally"><b>${backs} of ${dirs.length}</b> back you · confidence <b>${Math.round(desk.confidence)}</b> · <span>${esc(desk.verdict)}</span></div>`,
  { hold: 1200 + dirs.length * 220 });
  return p;
}

function cinemaIpoBell(info) {
  sfx("bell");
  return cnStage("cn-ipo", `
    <svg class="cn-bell" viewBox="0 0 120 120" aria-hidden="true"><g class="cn-swing"><path d="M60 14c-20 0-32 16-32 38v20l-10 14h84l-10-14V52c0-22-12-38-32-38z" fill="#e5b44f"/><circle cx="60" cy="96" r="8" fill="#b88a2e"/><rect x="54" y="6" width="12" height="10" rx="3" fill="#b88a2e"/></g></svg>
    <div class="cn-kicker">Opening bell</div>
    <h2 class="cn-title">${info.name ? `${esc(info.name)} is public` : "You're public"}</h2>
    <p class="cn-tag">${esc(info.message || "")}</p>`, { hold: 2400 });
}

window.cinemaBoardVote = cinemaBoardVote;

export { cinemaBoardVote, cinemaIpoBell, cinemaMomentIntro, cinemaMomentReveal };
